/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import {
  checkIfUserApplicationHasErrors,
  getApplication,
} from "Models/BlinkApplication";
import { getFullName } from "Models/User";
import { useOrgData } from "state/OrgDataContext";
import {
  Button,
  DocumentCard,
  DocumentViewer,
  FontAwesomeIcon,
  HR,
  Heading,
  Stack,
  Text,
} from "components";
import { LoadingDots } from "components/Loaders";
import useCompanion from "hooks/useCompanion";
import useTryCatch from "hooks/useTryCatch";
import { getFormCategories, getFormCategoryFields } from "lib/userApi";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ROUTES from "routes";
import { usePrefNavigator } from "state/PrefNavigatorContext";
import { PageLayout } from "templates";
import { normalizeToCamelCase } from "utils/capitalize";
import downloadFile from "utils/downloadFile";
import getBase64Details from "utils/getBase64Details";
import getFormattedDate from "utils/getFormattedDate";
import usePreviousRoute from "utils/usePreviousRoute";
import { useTranslation } from "react-i18next";
import { pushToDataLayer } from "utils/gtm";

// Redefined inside the component to use t()

const GuestDetailsPage = () => {
  const { t } = useTranslation();

  const BlinkDocsStates = {
    loading: {
      note: t("blinkDocs.states.loading"),
      state: "loading",
    },
    success: {
      note: t("blinkDocs.states.success"),
      state: "success",
    },
  };

  const formatPreference = (preference, namespace) => {
    if (!preference) return "";
    let pref = preference;

    if (preference?.startsWith(",other")) {
      pref = preference.slice(1);
    }
    return (
      pref
        .split(",")
        // it could be something like "other:some note"
        .map((item) => {
          if (item.startsWith("other:")) {
            return `${t("common.labels.other")}: ${item.replace("other:", "")}`;
          }
          return t(`${namespace}.${item}`, {
            defaultValue: item
              .replaceAll(":", ": ")
              .replace(/(?:^|\s)\S/g, (c) => c.toUpperCase()),
          });
        })
        .join(", ")
    );
  };

  const { userId, eventId } = useParams();
  const [companion] = useCompanion(userId, { stale: false });
  const [sections, setSections] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const blinkApplication = getApplication(companion);
  const { hasEnabledStep, enabledSteps } = usePrefNavigator();
  const { dataContent: EVENT_CONTENT } = useOrgData();

  const getUserCategory = useTryCatch(async () => {
    if (!companion?.registration_form_category_id) return;
    const api = await getFormCategories(eventId);
    const categoriesData = api.data;
    if (categoriesData.length > 0) {
      const matchingCategory = categoriesData?.find(
        (category) => category.id === companion?.registration_form_category_id,
      );
      if (matchingCategory) {
        const api = await getFormCategoryFields(matchingCategory.id, eventId);
        if (api.ok) {
          setSections(api?.data?.configuration_obj);
        }
      }
    }
  }, setIsLoading);

  useEffect(() => {
    getUserCategory();
  }, [companion]);

  const birthDate = getFormattedDate(companion?.birth_date);

  const goBack = usePreviousRoute("/dashboard");
  const mapPrefixValue = (prefix) =>
    normalizeToCamelCase(prefix) === "preferNotToSay" || (prefix ?? "") === ""
      ? ""
      : prefix;

  const isNameFieldVisible = (sections) => {
    const personalInfoFields = sections?.["personal_info"]?.fields;
    return (
      personalInfoFields?.first_name?.is_visible ||
      personalInfoFields?.last_name?.is_visible
    );
  };

  const formatGuestName = (companion, sections) => {
    const shouldShowPrefix =
      sections?.["personal_info"]?.fields?.prefix?.is_visible;

    if (!shouldShowPrefix || !companion?.prefix) {
      return getFullName(companion);
    }

    return `${mapPrefixValue(companion.prefix)} ${getFullName(companion)}`;
  };

  const GuestNameDisplay = ({ companion, sections }) => {
    const { t } = useTranslation();
    if (!isNameFieldVisible(sections)) {
      return null;
    }

    return (
      <Stack className="gap-y-[5px]">
        <Text
          as="span"
          className="text-sm -text--theme-colors-text-subtitle-brand line-clamp-1 w-full text-ellipsis break-all"
        >
          {t("guestDetails.formFields.nameLabel")}
        </Text>

        <Text className="text-lg -text--theme-colors-text-body line-clamp-1 w-full text-ellipsis break-all">
          {formatGuestName(companion, sections)}
        </Text>
      </Stack>
    );
  };

  return (
    <PageLayout title={t("guestDetails.title")} hasGoBack goBackMethod={goBack}>
      <Stack className="mt-10 md:mt-16 gap-y-10 md:mb-40">
        {companion && !isLoading && sections ? (
          <>
            <Stack className="w-full -bg--theme-colors-surface-neutral-level02 border -border--theme-colors-border-neutral-11 -rounded-outer-container px-6 py-10 md:px-[54px]">
              <Heading
                as="h2"
                className={"-text--theme-colors-text-title-brand mb-5"}
              >
                {companion?.hasOwnProperty("hash_id") &&
                companion.hash_id.length > 0
                  ? t("guestDetails.guestInfoLabel")
                  : t("guestDetails.companionApplicantLabel")}
              </Heading>
              <Stack as="section" className="space-y-8">
                {checkIfUserApplicationHasErrors(companion) && (
                  <div className="flex flex-col lg:flex-row justify-between md:items-center w-full">
                    <p className="-text--theme-colors-danger-default text-sm">
                      {t("guestDetails.issuesInstructions")}
                    </p>
                    <div className="mt-5 lg:mt-0 min-w-fit">
                      <Button
                        as="link"
                        to={ROUTES.dashboard.reviewInfo(userId)}
                        variant="primary"
                        size="lg"
                      >
                        {t("guestDetails.fixIssuesButtonLabel")}
                      </Button>
                    </div>
                  </div>
                )}
                {sections ? (
                  <div className="flex flex-col md:flex-row md:items-center max-md:gap-y-8">
                    {sections["documents"]?.fields?.image?.is_visible && (
                      <>
                        <DocumentViewer
                          {...getBase64Details(
                            // MD / VIP app photo updates land in `image`/`avatar`, not
                            // `personal_image` (registration upload), so prefer them to
                            // match the dashboard card. card-internal-data only returns `avatar`.
                            companion?.image ||
                              companion?.avatar ||
                              companion?.personal_image,
                          )}
                          alt="user_image"
                          className="w-64 h-64 object-cover max-md:self-center max-md:mt-5 -rounded-image"
                        />
                        <div className="self-stretch hidden md:block border-e -border--theme-colors-border-neutral-11 mx-10"></div>
                      </>
                    )}
                    <div className="grid md:grid-cols-2 gap-12">
                      <GuestNameDisplay
                        companion={companion}
                        sections={sections}
                      />

                      {sections["personal_info"]?.fields?.organization
                        ?.is_visible && (
                        <Stack className="gap-y-[5px]">
                          <Text
                            as="span"
                            className="text-sm -text--theme-colors-text-subtitle-brand"
                          >
                            {t("guestDetails.formFields.guestType")}
                          </Text>
                          <Text className="text-lg -text--theme-colors-text-body line-clamp-1 w-full text-ellipsis break-all">
                            {companion?.organization}
                          </Text>
                        </Stack>
                      )}

                      {sections["contact_info"]?.fields?.email?.is_visible && (
                        <Stack className="gap-y-[5px]">
                          <>
                            <Text
                              as="span"
                              className="text-sm -text--theme-colors-text-subtitle-brand"
                            >
                              {t("guestDetails.formFields.emailLabel")}
                            </Text>
                            <Text className="text-sm  -text--theme-colors-text-body">
                              {companion?.email}
                            </Text>
                          </>
                        </Stack>
                      )}
                      {sections["contact_info"]?.fields
                        ?.emergency_contact_full_name?.is_visible && (
                        <Stack className="gap-y-[5px]">
                          <>
                            <Text
                              as="span"
                              className="text-sm -text--theme-colors-text-subtitle-brand"
                            >
                              {t(
                                "guestDetails.formFields.emergencyContactLabel",
                              )}
                            </Text>
                            <Text className="text-lg -text--theme-colors-text-body">
                              {companion?.emergency_contact_full_name || "-"}
                            </Text>
                          </>
                        </Stack>
                      )}

                      {sections["personal_info"]?.fields?.birth_date
                        ?.is_visible && (
                        <Stack className="gap-y-[5px]">
                          <>
                            <Text
                              as="span"
                              className="text-sm -text--theme-colors-text-subtitle-brand"
                            >
                              {t("guestDetails.formFields.dateOfBirthLabel")}
                            </Text>
                            <Text className="text-lg -text--theme-colors-text-body">
                              {birthDate}
                            </Text>
                          </>
                        </Stack>
                      )}

                      {sections["contact_info"]?.fields?.contact_number
                        ?.is_visible && (
                        <Stack className="gap-y-[5px]">
                          <>
                            <Text
                              as="span"
                              className="text-sm -text--theme-colors-text-subtitle-brand"
                            >
                              {t("guestDetails.formFields.phoneNumberLabel")}
                            </Text>
                            <Text className="text-lg -text--theme-colors-text-body rtl:!text-start [direction:ltr]">
                              {companion?.country_code &&
                              companion?.contact_number
                                ? "+" +
                                  companion?.country_code +
                                  " " +
                                  companion?.contact_number
                                : "-"}
                            </Text>
                          </>
                        </Stack>
                      )}

                      {sections["contact_info"]?.fields
                        ?.emergency_contact_phone_number?.is_visible && (
                        <>
                          <Stack className="gap-y-[5px]">
                            <Text
                              as="span"
                              className="text-sm -text--theme-colors-text-subtitle-brand"
                            >
                              {t(
                                "guestDetails.formFields.emergencyContactNumberLabel",
                              )}
                            </Text>
                            <Text className="text-lg -text--theme-colors-text-body rtl:!text-start [direction:ltr]">
                              {companion?.emergency_contact_country_code &&
                                companion?.emergency_contact_phone_number &&
                                "+" +
                                  companion?.emergency_contact_country_code}{" "}
                              {companion?.emergency_contact_phone_number ||
                                "- "}
                            </Text>
                          </Stack>
                        </>
                      )}

                      {sections["contact_info"]?.fields?.assistant_email
                        ?.is_visible && (
                        <Stack className="gap-y-[5px]">
                          <Text
                            as="span"
                            className="text-sm -text--theme-colors-text-subtitle-brand"
                          >
                            {t("guestDetails.formFields.assistantEmailLabel")}
                          </Text>
                          <Text className="text-sm -text--theme-colors-text-body">
                            {companion?.assistant_email || "-"}
                          </Text>
                        </Stack>
                      )}

                      {sections["contact_info"]?.fields
                        ?.assistant_contact_number?.is_visible && (
                        <Stack className="gap-y-[5px]">
                          <Text
                            as="span"
                            className="text-sm -text--theme-colors-text-subtitle-brand"
                          >
                            {t(
                              "guestDetails.formFields.assistantContactNumberLabel",
                            )}
                          </Text>
                          <Text className="text-lg -text--theme-colors-text-body rtl:!text-start [direction:ltr]">
                            {companion?.assistant_country_code &&
                            companion?.assistant_contact_number
                              ? "+" +
                                companion?.assistant_country_code +
                                " " +
                                companion?.assistant_contact_number
                              : "-"}
                          </Text>
                        </Stack>
                      )}
                    </div>
                  </div>
                ) : (
                  <section className="mt-10 max-w-[800px] m-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 place-items-center gap-10">
                    <LoadingDots />
                  </section>
                )}
                {blinkApplication?.blink_id && (
                  <>
                    <HR />

                    <Stack className="gap-y-5">
                      <Heading
                        as="h2"
                        className="-text--theme-colors-text-title-brand"
                      >
                        {t("guestDetails.formFields.userIdLabel")}
                      </Heading>
                      <div className="flex flex-col lg:flex-row items-center space-y-2.5 lg:space-y-0 lg:gap-x-8">
                        <Stack className="gap-y-[5px]">
                          <Text
                            as="span"
                            className="text-sm -text--theme-colors-text-subtitle-brand"
                          >
                            {t("guestDetails.formFields.userCardNumberLabel")}
                          </Text>
                          <Text className="text-lg -text--theme-colors-text-body capitalize">
                            {blinkApplication.blink_id}
                          </Text>
                        </Stack>
                      </div>
                    </Stack>
                  </>
                )}
              </Stack>
            </Stack>
            {hasEnabledStep && (
              <Stack className="w-full -bg--theme-colors-surface-neutral-level02 border -border--theme-colors-border-neutral-11 -rounded-outer-container px-6 py-10 md:px-[54px] gap-y-10">
                <div className="flex w-full justify-between">
                  <Heading
                    as="h2"
                    className="-text--theme-colors-text-title-brand"
                  >
                    {t("specialConcerns.title")}
                  </Heading>
                  <div
                    className="hidden md:block"
                    onClick={() => {
                      pushToDataLayer({
                        event: "view_special_concerns",
                        screen_name: "special concerns",
                        journey_step: "special concerns",
                      });
                    }}
                  >
                    <Button
                      as="link"
                      variant="primary"
                      to={ROUTES.dashboard.guestPreferences(userId)}
                    >
                      <div className="flex gap-x-s15 items-center">
                        <span>{t("specialConcerns.viewButtonLabel")}</span>
                        <FontAwesomeIcon name="fa-eye" />
                      </div>
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mb-6">
                  {enabledSteps.some((step) => step.step === "preferences") && (
                    <div>
                      <Heading
                        as="h4"
                        isBold={false}
                        className="-text--theme-colors-text-subtitle-brand"
                      >
                        {t("preferences.title")}
                      </Heading>
                      <p className="-text--theme-colors-text-body">
                        {formatPreference(
                          companion?.food_preferences,
                          "preferences",
                        ) || "-"}
                      </p>
                    </div>
                  )}
                  {enabledSteps.some((step) => step.step === "notes") && (
                    <div>
                      <Heading
                        as="h4"
                        isBold={false}
                        className="-text--theme-colors-text-subtitle-brand"
                      >
                        {t("notes.specialAssistanceLabel")}
                      </Heading>
                      <p className="-text--theme-colors-text-body">
                        {companion?.special_medical_assistance}
                      </p>
                    </div>
                  )}
                  {enabledSteps.some(
                    (step) => step.step === "generalAllergies",
                  ) && (
                    <div>
                      <Heading
                        as="h4"
                        isBold={false}
                        className="-text--theme-colors-text-subtitle-brand"
                      >
                        {t("generalAllergies.title")}
                      </Heading>
                      <p className="-text--theme-colors-text-body">
                        {!companion?.general_allergies &&
                        !companion?.other_allergies &&
                        !companion?.chemical_allergies
                          ? "-"
                          : formatPreference(
                              companion?.general_allergies,
                              "generalAllergies",
                            ) +
                            (companion?.chemical_allergies
                              ? ` ${t("generalAllergies.chemicals")}: ` +
                                formatPreference(
                                  companion?.chemical_allergies,
                                  "generalAllergies",
                                )
                              : "") +
                            (companion?.other_allergies
                              ? ` ${t("common.labels.other")}: ` +
                                formatPreference(
                                  companion?.other_allergies,
                                  "generalAllergies",
                                )
                              : "")}
                      </p>
                    </div>
                  )}

                  {enabledSteps.some((step) => step.step === "notes") && (
                    <div>
                      <Heading
                        as="h4"
                        isBold={false}
                        className="-text--theme-colors-text-subtitle-brand"
                      >
                        {t("notes.otherNotesLabel")}
                      </Heading>
                      <p className="-text--theme-colors-text-body">
                        {companion?.other_notes}
                      </p>
                    </div>
                  )}

                  {enabledSteps.some((step) => step.step === "allergies") && (
                    <div>
                      <Heading
                        as="h4"
                        isBold={false}
                        className="-text--theme-colors-text-subtitle-brand"
                      >
                        {t("allergies.dashboardTitle")}
                      </Heading>
                      <p className="-text--theme-colors-text-body">
                        {formatPreference(companion?.allergies, "allergies") ||
                          "-"}
                      </p>
                    </div>
                  )}
                </div>

                <div className="block md:hidden">
                  <Button
                    as="link"
                    variant="primary"
                    to={ROUTES.dashboard.guestPreferences(userId)}
                  >
                    <div className="flex gap-x-s15 items-center">
                      <span>{t("dashboard.editButtonLabel")}</span>
                      <FontAwesomeIcon name="fa-pen" />
                    </div>
                  </Button>
                </div>
              </Stack>
            )}
            {companion?.documents && companion?.documents.length > 0 && (
              <Stack
                as="section"
                className="w-full -bg--theme-colors-surface-neutral-level02 border -border--theme-colors-border-neutral-11 -rounded-outer-container px-6 py-10 md:px-[54px] gap-y-10"
              >
                <div className="flex flex-col justify-between">
                  <Heading
                    className="-text--theme-colors-text-title-brand"
                    as="h2"
                  >
                    {t("dashboard.tripDocumentsLabel")}
                  </Heading>
                  <div>
                    <p className="-text--theme-colors-text-body">
                      {EVENT_CONTENT.dashboard.tripDocumentsInstructions}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {companion.documents &&
                    companion.documents.map((doc, index) => (
                      <Stack key={doc.title}>
                        <DocumentCard
                          title={doc.title || doc.description}
                          media={DocumentIconQuery(doc.icon)}
                          variant="downloadable"
                          note={BlinkDocsStates["success"].note}
                          state={BlinkDocsStates["success"].state}
                          onClick={() => downloadFile(doc.url)}
                          onIconClick={() => downloadFile(doc.url)}
                        />
                      </Stack>
                    ))}
                </div>
              </Stack>
            )}
          </>
        ) : (
          <div className="flex justify-center items-center h-[300px]">
            <LoadingDots />
          </div>
        )}
      </Stack>
    </PageLayout>
  );
};

export default GuestDetailsPage;

const DocumentIconQuery = (name) =>
  ({
    hotel:
      "https://res.cloudinary.com/dpfuggind/image/upload/v1665345565/assets/hotel_phykaf.svg",
    permit:
      "https://res.cloudinary.com/dpfuggind/image/upload/v1665345565/assets/permit_rorlft.svg",
  })[name?.toLowerCase()] ||
  "https://res.cloudinary.com/dpfuggind/image/upload/v1665345609/assets/info_od4qbx.svg";
